import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * AdminPesdbRelink — ri-aggancio per NOME dei giocatori in rosa il cui id PESDB è stato
 * rimosso (id morto). L'admin cerca su PESDB per nome, sceglie il nuovo id e ri-aggancia.
 * Da eseguire in PREVIEW; poi Esporta → Importa in Produzione.
 */
export default function AdminPesdbRelink() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(null); // old_pes_id aperto
    const [search, setSearch] = useState({}); // old_pes_id -> { term, candidates, loading, note }
    const [importResult, setImportResult] = useState(null);
    const [autoJob, setAutoJob] = useState(null);
    const [applyJob, setApplyJob] = useState(null);
    const [ghosts, setGhosts] = useState(null); // { count, items }
    const [ghostMsg, setGhostMsg] = useState("");

    const loadGhosts = async () => {
        try {
            const { data } = await api.get("/admin/pesdb-relink/ghosts");
            setGhosts(data);
        } catch { /* noop */ }
    };

    const cleanGhosts = async () => {
        if (!window.confirm(`Pulire ${ghosts?.count ?? 0} record fantasma? Spariranno dalla ricerca e dalle liste.`)) return;
        setBusy(true);
        setGhostMsg("");
        try {
            const { data } = await api.post("/admin/pesdb-relink/ghosts/clean");
            setGhostMsg(`✓ ${data.cleaned} record fantasma puliti.`);
            await loadGhosts();
        } catch (e) {
            setErr(e.response?.data?.detail || "Errore pulizia fantasmi");
        } finally {
            setBusy(false);
        }
    };

    const loadAutoStatus = async () => {
        try {
            const { data } = await api.get("/admin/pesdb-relink/auto-status");
            setAutoJob(data);
            return data;
        } catch { return null; }
    };

    const loadApplyStatus = async () => {
        try {
            const { data } = await api.get("/admin/pesdb-relink/apply-status");
            setApplyJob(data);
            return data;
        } catch { return null; }
    };

    const startApplySuggested = async () => {
        setErr("");
        try {
            await api.post("/admin/pesdb-relink/apply-suggested");
            await loadApplyStatus();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        }
    };

    const cancelApplySuggested = async () => {
        try { await api.post("/admin/pesdb-relink/apply-suggested/cancel"); } catch { /* noop */ }
        await loadApplyStatus();
    };

    const startAuto = async () => {
        setErr("");
        try {
            await api.post("/admin/pesdb-relink/auto-search");
            await loadAutoStatus();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        }
    };

    const cancelAuto = async () => {
        try { await api.post("/admin/pesdb-relink/auto-search/cancel"); } catch { /* noop */ }
        await loadAutoStatus();
    };

    // poll dello stato job + ricarica lista quando un job in corso termina
    useEffect(() => {
        let autoWas = false;
        let applyWas = false;
        const tick = async () => {
            const [j, a] = await Promise.all([loadAutoStatus(), loadApplyStatus()]);
            let finished = false;
            if (j) {
                if (j.running) autoWas = true;
                else if (autoWas) { autoWas = false; finished = true; }
            }
            if (a) {
                if (a.running) applyWas = true;
                else if (applyWas) { applyWas = false; finished = true; }
            }
            if (finished) await load();
        };
        tick();
        const t = setInterval(tick, 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/pesdb-relink/list");
            setData(data);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); loadGhosts(); }, []);
    const toggle = (item) => {
        const id = item.old_pes_id;
        if (open === id) { setOpen(null); return; }
        setOpen(id);
        if (!search[id]) setSearch((s) => ({ ...s, [id]: { term: item.name, candidates: null, loading: false, note: "" } }));
    };

    const runSearch = async (oldId) => {
        const term = search[oldId]?.term || "";
        setSearch((s) => ({ ...s, [oldId]: { ...s[oldId], loading: true, note: "" } }));
        try {
            const { data } = await api.get("/admin/pesdb-relink/search", { params: { name: term } });
            setSearch((s) => ({ ...s, [oldId]: { ...s[oldId], candidates: data.candidates, loading: false, note: data.note || "" } }));
        } catch (e) {
            setSearch((s) => ({ ...s, [oldId]: { ...s[oldId], loading: false, candidates: [], note: e.response?.data?.detail || "Errore ricerca" } }));
        }
    };

    const apply = async (oldId, newId, cand) => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.post("/admin/pesdb-relink/apply", {
                old_pes_id: oldId, new_pes_id: newId,
                authentic_url: cand?.authentic_url || null, name: cand?.name || null,
            });
            setOpen(null);
            await load();
            return data;
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const exportRelinks = async () => {
        setErr("");
        setBusy(true);
        try {
            const { data } = await api.get("/admin/pesdb-relink/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "riaggancio-pesdb.json";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const importRelinks = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        setErr("");
        setImportResult(null);
        setBusy(true);
        try {
            const parsed = JSON.parse(await file.text());
            const items = Array.isArray(parsed) ? parsed : parsed.items || [];
            const { data } = await api.post("/admin/pesdb-relink/import", { items });
            setImportResult(data);
            await load();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message || "File non valido");
        } finally {
            setBusy(false);
        }
    };

    const s = data?.summary;

    return (
        <div data-testid="admin-pesdb-relink">
            <div className="op-label text-tff7a1a mb-1">// RI-AGGANCIA PESDB (ID RIMOSSI)</div>
            <h1 className="op-heading text-3xl text-tffffff mb-2">Ri-aggancia per nome</h1>
            <p className="text-t8f95a3 text-sm max-w-3xl mb-6 leading-relaxed">
                Giocatori in rosa il cui id PESDB è stato rimosso (id morto): esistono ancora su PESDB con un
                <strong> id nuovo</strong>. Cerca per nome, scegli il candidato giusto e ri-aggancia.
                Eseguire in <strong>PREVIEW</strong>, poi <strong>Esporta</strong> e <strong>Importa in Produzione</strong>.
                ⚠️ pesdb.net mette in rate-limit dopo poche richieste: se la ricerca fallisce, attendi ~30s e riprova.
            </p>

            {err && <div className="op-card p-3 border-tff3b30 text-tff3b30 text-sm mb-4" data-testid="relink-error">{err}</div>}

            <div className="flex gap-3 mb-6 flex-wrap">
                <StatPill label="Da ri-agganciare" value={s?.remaining ?? 0} accent="rgb(var(--tc-ff3b30))" testId="relink-remaining" />
                <StatPill label="Già ri-agganciati" value={s?.relinked ?? 0} accent="rgb(var(--tc-7ce38b))" testId="relink-done" />
                <StatPill label="Totale id morti" value={s?.total ?? 0} accent="rgb(var(--tc-8f95a3))" testId="relink-total" />
            </div>

            {/* Ricerca automatica di tutti gli id morti */}
            <div className="op-card p-4 mb-6" data-testid="relink-auto-panel">
                <div className="op-label text-te8ff00 mb-2">// RICERCA AUTOMATICA (PREVIEW)</div>
                <p className="text-t8f95a3 text-sm mb-3 leading-relaxed">
                    Cerca su PESDB per nome <strong>tutti</strong> gli id morti, con pause anti rate-limit, e
                    pre-seleziona il candidato con <strong>corrispondenza esatta</strong>: poi ti basta confermare con un clic.
                    Se pesdb va in rate-limit, alcuni restano da cercare: ri-lancia il pulsante (riprende da dove era).
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                    {autoJob?.running ? (
                        <button onClick={cancelAuto}
                            className="op-btn border border-tff3b30 text-tff3b30 hover:bg-tff3b30 hover:text-black px-4 py-2 transition-colors"
                            data-testid="relink-auto-cancel-btn">⏹ Ferma ricerca</button>
                    ) : (
                        <button onClick={startAuto}
                            className="op-btn border border-te8ff00 text-te8ff00 hover:bg-te8ff00 hover:text-black px-4 py-2 transition-colors"
                            data-testid="relink-auto-start-btn">🔎 Cerca tutti automaticamente</button>
                    )}
                    {autoJob && (autoJob.total > 0 || autoJob.running) && (
                        <span className="op-mono text-[12px] text-tc9ccd3" data-testid="relink-auto-progress">
                            {autoJob.running ? "In corso… " : "Ultima ricerca: "}
                            {autoJob.done ?? 0}/{autoJob.total ?? 0} cercati · {autoJob.found ?? 0} match esatti trovati
                        </span>
                    )}
                </div>
                {autoJob && autoJob.total > 0 && (
                    <div className="w-full h-1.5 bg-t22252a mt-3 rounded-full overflow-hidden">
                        <div className="h-full bg-te8ff00 transition-all"
                            style={{ width: `${Math.round(100 * (autoJob.done || 0) / Math.max(1, autoJob.total))}%` }} />
                    </div>
                )}

                {/* Aggancio automatico dei match esatti */}
                <div className="border-t border-t22252a mt-4 pt-4">
                    <div className="flex items-center gap-3 flex-wrap">
                        {applyJob?.running ? (
                            <button onClick={cancelApplySuggested}
                                className="op-btn border border-tff3b30 text-tff3b30 hover:bg-tff3b30 hover:text-black px-4 py-2 transition-colors"
                                data-testid="relink-apply-cancel-btn">⏹ Ferma ri-aggancio</button>
                        ) : (
                            <button onClick={startApplySuggested}
                                disabled={autoJob?.running || !(autoJob?.found > 0 || applyJob?.total > 0)}
                                className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-4 py-2 disabled:opacity-40 transition-colors"
                                data-testid="relink-apply-start-btn">⚡ Ri-aggancia tutti i match esatti</button>
                        )}
                        {applyJob && (applyJob.total > 0 || applyJob.running) && (
                            <span className="op-mono text-[12px] text-tc9ccd3" data-testid="relink-apply-progress">
                                {applyJob.running ? "Aggancio in corso… " : "Ultimo aggancio: "}
                                {applyJob.done ?? 0}/{applyJob.total ?? 0} · {applyJob.applied ?? 0} agganciati
                                {applyJob.failed ? ` · ${applyJob.failed} falliti` : ""}
                            </span>
                        )}
                    </div>
                    {applyJob && applyJob.total > 0 && (
                        <div className="w-full h-1.5 bg-t22252a mt-3 rounded-full overflow-hidden">
                            <div className="h-full bg-t7ce38b transition-all"
                                style={{ width: `${Math.round(100 * (applyJob.done || 0) / Math.max(1, applyJob.total))}%` }} />
                        </div>
                    )}
                    <p className="op-mono text-[10px] text-t5c6370 mt-2 leading-relaxed">
                        Applica in un colpo solo tutti i giocatori con corrispondenza esatta trovata dalla ricerca.
                        Scarica i dati del nuovo id con pause anti rate-limit. Poi usa il PONTE qui sotto per esportare e caricare in produzione.
                    </p>
                </div>
            </div>

            {/* Ponte Preview → Produzione */}
            <div className="op-card p-4 mb-6" data-testid="relink-bridge">
                <div className="op-label text-t2bb6ff mb-2">// PONTE PREVIEW → PRODUZIONE</div>
                <div className="flex flex-wrap items-center gap-3">
                    <button onClick={exportRelinks} disabled={busy}
                        className="op-btn border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff hover:text-black px-4 py-2 disabled:opacity-50 transition-colors"
                        data-testid="relink-export-btn">⬇ Esporta ri-aganci (JSON)</button>
                    <label className="op-btn border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black px-4 py-2 cursor-pointer transition-colors"
                        data-testid="relink-import-label">⬆ Importa in Produzione
                        <input type="file" accept="application/json,.json" onChange={importRelinks} disabled={busy} className="hidden" data-testid="relink-import-input" />
                    </label>
                    {importResult && (
                        <span className="op-mono text-[12px] text-t7ce38b" data-testid="relink-import-result">
                            ✓ {importResult.applied} applicati · {importResult.roster_entries_updated} voci rosa aggiornate
                        </span>
                    )}
                </div>
            </div>

            {/* Pulizia fantasmi PESDB */}
            <div className="op-card p-4 mb-6" data-testid="ghost-clean-panel">
                <div className="op-label text-tff7a1a mb-2">// PULIZIA FANTASMI PESDB</div>
                <p className="text-t8f95a3 text-sm mb-3 leading-relaxed">
                    Record di giocatori <strong>già riagganciati</strong> (l'id vecchio punta a uno nuovo)
                    ma non ancora nascosti: comparivano nella ricerca come doppioni <strong>senza foto/dati</strong>.
                    Questo strumento li marca come duplicati così spariscono ovunque. Operazione sicura e ripetibile.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={cleanGhosts} disabled={busy || !ghosts?.count}
                        className="op-btn border border-tff7a1a text-tff7a1a hover:bg-tff7a1a hover:text-black px-4 py-2 disabled:opacity-40 transition-colors"
                        data-testid="ghost-clean-btn">🧹 Pulisci {ghosts?.count ?? 0} fantasmi</button>
                    {ghostMsg && <span className="op-mono text-[12px] text-t7ce38b" data-testid="ghost-clean-result">{ghostMsg}</span>}
                    {ghosts?.count === 0 && !ghostMsg && (
                        <span className="op-mono text-[12px] text-t5c6370" data-testid="ghost-clean-empty">Nessun fantasma da pulire ✓</span>
                    )}
                </div>
                {ghosts?.items?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="ghost-list">
                        {ghosts.items.slice(0, 30).map((g) => (
                            <span key={g.old_pes_id} className="op-mono text-[11px] text-tc9ccd3 bg-t1a1d22 border border-t22252a rounded px-2 py-1">
                                {g.name || "—"} <span className="text-t5c6370">#{g.old_pes_id}→{g.relinked_to}</span>
                            </span>
                        ))}
                        {ghosts.items.length > 30 && (
                            <span className="op-mono text-[11px] text-t5c6370 px-2 py-1">+{ghosts.items.length - 30} altri…</span>
                        )}
                    </div>
                )}
            </div>

            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse">// caricamento…</div>
            ) : (data?.items?.length === 0 ? (
                <div className="op-card p-5 text-t7ce38b text-sm" data-testid="relink-empty">
                    Nessun giocatore con id PESDB rimosso. 🎉 (Lancia prima il sync mirato in "PES DB Sync".)
                </div>
            ) : (
                <div className="space-y-2" data-testid="relink-list">
                    {data.items.map((item) => (
                        <div key={item.old_pes_id} className="op-card overflow-hidden" data-testid={`relink-item-${item.old_pes_id}`}>
                            <button onClick={() => toggle(item)}
                                className="w-full flex items-center justify-between gap-3 p-4 hover:bg-t1a1d24 transition-colors text-left"
                                data-testid={`relink-toggle-${item.old_pes_id}`}>
                                <div className="min-w-0">
                                    <div className="op-heading text-lg text-tffffff truncate">{item.name || "—"}</div>
                                    <div className="op-mono text-[11px] text-t8f95a3">
                                        id morto {item.old_pes_id} · {item.count} in rosa · {item.teams.join(", ") || "—"}
                                    </div>
                                </div>
                                {item.relinked_to ? (
                                    <span className="op-mono text-[10px] uppercase px-2 py-1 border border-t7ce38b text-t7ce38b shrink-0" data-testid={`relink-status-${item.old_pes_id}`}>
                                        ✓ → {item.relinked_to}
                                    </span>
                                ) : item.suggested_pes_id ? (
                                    <span
                                        role="button"
                                        onClick={(e) => { e.stopPropagation(); apply(item.old_pes_id, item.suggested_pes_id, (item.candidates || []).find((c) => c.pes_id === item.suggested_pes_id)); }}
                                        className="op-mono text-[10px] uppercase px-2 py-1 border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black shrink-0 transition-colors cursor-pointer"
                                        data-testid={`relink-confirm-${item.old_pes_id}`}
                                        title={`Match esatto trovato (id ${item.suggested_pes_id}) — clic per agganciare`}
                                    >
                                        ✓ Conferma → {item.suggested_pes_id}
                                    </span>
                                ) : (
                                    <span className="op-mono text-[10px] uppercase px-2 py-1 border border-tff7a1a text-tff7a1a shrink-0">
                                        {open === item.old_pes_id ? "Chiudi" : "Cerca ▸"}
                                    </span>
                                )}
                            </button>

                            {open === item.old_pes_id && (() => {
                                const cands = search[item.old_pes_id]?.candidates ?? item.candidates;
                                const note = search[item.old_pes_id]?.note;
                                return (
                                <div className="p-4 border-t border-t22252a bg-t0e1014">
                                    <div className="flex gap-2 mb-3">
                                        <input
                                            value={search[item.old_pes_id]?.term ?? ""}
                                            onChange={(e) => setSearch((st) => ({ ...st, [item.old_pes_id]: { ...st[item.old_pes_id], term: e.target.value } }))}
                                            placeholder="Nome da cercare su PESDB"
                                            className="op-input op-mono text-sm flex-1"
                                            data-testid={`relink-search-input-${item.old_pes_id}`}
                                        />
                                        <button onClick={() => runSearch(item.old_pes_id)} disabled={search[item.old_pes_id]?.loading}
                                            className="op-btn border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff hover:text-black px-4 disabled:opacity-50 transition-colors"
                                            data-testid={`relink-search-btn-${item.old_pes_id}`}>
                                            {search[item.old_pes_id]?.loading ? "…" : "Cerca su PESDB"}
                                        </button>
                                    </div>
                                    {note && <div className="op-mono text-[11px] text-tff7a1a mb-2">{note}</div>}
                                    {Array.isArray(cands) && (
                                        cands.length === 0 ? (
                                            <div className="op-mono text-[12px] text-t8f95a3">Nessun candidato. Prova un nome diverso.</div>
                                        ) : (
                                            <div className="space-y-1">
                                                {cands.map((c) => {
                                                    const isSuggested = c.pes_id === item.suggested_pes_id;
                                                    return (
                                                    <div key={c.pes_id}
                                                        className="flex items-center justify-between gap-3 px-3 py-2 border transition-colors"
                                                        style={{ borderColor: isSuggested ? "rgb(var(--tc-7ce38b))" : "rgb(var(--tc-22252a))" }}
                                                        data-testid={`relink-candidate-${item.old_pes_id}-${c.pes_id}`}>
                                                        <div className="op-mono text-xs text-tc9ccd3 min-w-0 truncate">
                                                            {isSuggested && <span className="text-t7ce38b mr-1">★</span>}
                                                            <span className="text-tffffff">{c.name}</span> · {c.position || "—"} · OVR {c.overall ?? "—"}{c.max_level ? ` (MAX ${c.max_level})` : ""}{c.card_type ? ` · ${c.card_type}` : ""} <span className="text-t5c6370">(id {c.pes_id})</span>
                                                        </div>
                                                        <button onClick={() => apply(item.old_pes_id, c.pes_id, c)} disabled={busy}
                                                            className="op-btn px-3 py-1 text-xs shrink-0 disabled:opacity-50 transition-colors border border-t7ce38b text-t7ce38b hover:bg-t7ce38b hover:text-black"
                                                            data-testid={`relink-apply-${item.old_pes_id}-${c.pes_id}`}>
                                                            {isSuggested ? "Aggancia ★" : "Aggancia"}
                                                        </button>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        )
                                    )}
                                </div>
                                );
                            })()}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function StatPill({ label, value, accent, testId }) {
    return (
        <div className="border border-t22252a px-4 py-3 bg-t0e1014" data-testid={testId}>
            <div className="op-label text-[10px]">{label}</div>
            <div className="op-mono font-bold text-2xl" style={{ color: accent }}>{(value ?? 0).toLocaleString("it-IT")}</div>
        </div>
    );
}
